import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import styles from './discounts.module.scss';
import { config } from '../../api/config';
import { actions } from '../../state';
import { serviceTypes, mapServiceTypes } from '../../utils';
import {
  Checkbox,
  Divider,
  Icon,
  Input,
  MultiSelectDropdown,
  MultiselectAll,
  Select,
  SubmitActions,
  WithSpinner,
} from '../../components';
import {
  ALL_SERVICES_VALUE,
  availableWeekdays,
  bannerPlacementOptions,
  discountUsageTypes,
} from './discounts.data';
import { LIMITS } from './discounts.validation';

const createEmptyServiceRow = () => ({
  serviceType: '',
  discount: '',
});

const Form = ({
  dispatch,
  values,
  onChange,
  onSubmit,
  onBannerChange,
  loading,
  codeDisabled = false,
  weekdays,
  setWeekdays,
  services,
  setServices,
  locations,
  setLocations,
  availableLocations,
  locationsLoading,
  groups,
  setGroups,
  clientGroups,
  clientGroupsLoading,
  setCreateDiscountBanner,
  discountBanner,
  bannerLoading,
}) => {
  const bannerInputRef = useRef(null);
  const isFormDisabled = loading;
  const isMultiUseDiscount = Number(values.type) === 192;

  const fetchLocations = useCallback(() => {
    dispatch(
      actions.locations.getLocationsAction({
        order: [
          {
            by: 'full_address',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  const fetchClientGroups = useCallback(() => {
    dispatch(
      actions.clientGroups.getClientGroupsAction({
        page: 1,
        pageSize: 1000,
        filter: {
          and: [
            {
              field: 'status',
              value: [1],
            },
          ],
        },
        order: [
          {
            by: 'name',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    fetchLocations();
    fetchClientGroups();
  }, [fetchClientGroups, fetchLocations]);

  useEffect(() => {
    const currentBannerId =
      values.announcement?.banner_id || values.announcement?.banner?.id || 0;

    if (discountBanner?.id && discountBanner.id !== currentBannerId) {
      onBannerChange(discountBanner);
    }

    if (
      !discountBanner?.id &&
      !Object.keys(discountBanner || {}).length &&
      currentBannerId
    ) {
      onBannerChange(null);
    }
  }, [discountBanner, onBannerChange, values.announcement]);

  const handleBannerFileChange = event => {
    const file = event.target.files?.[0];

    if (!file) return;

    dispatch(actions.discounts.uploadDiscountBanner(file));
  };

  const handleBannerRemove = () => {
    dispatch(actions.discounts.setDiscountBannerAction({}));
    onBannerChange(null);

    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
  };

  const openBannerInput = () => {
    if (bannerInputRef.current) {
      bannerInputRef.current.click();
    }
  };

  const bannerFile = values.announcement?.banner || discountBanner || null;
  const bannerFileName = bannerFile?.filename || bannerFile?.name || '';
  const bannerFileUrl = bannerFile?.url || '';

  const clientGroupOptions = useMemo(
    () => clientGroups.map(group => ({ id: group.id, name: group.name })),
    [clientGroups]
  );
  const selectedClientGroupOptions = useMemo(
    () => clientGroupOptions.filter(group => groups.includes(group.id)),
    [clientGroupOptions, groups]
  );
  const selectedLocationOptions = useMemo(
    () =>
      availableLocations.filter(location => locations.includes(location.id)),
    [availableLocations, locations]
  );

  const serviceRows = useMemo(
    () => (services.length ? services : [createEmptyServiceRow()]),
    [services]
  );

  const selectedServiceValues = useMemo(
    () =>
      serviceRows
        .map(service => service?.serviceType)
        .filter(
          serviceType =>
            serviceType !== ALL_SERVICES_VALUE &&
            serviceType !== '' &&
            serviceType !== null &&
            serviceType !== undefined
        )
        .map(Number),
    [serviceRows]
  );

  const isAllServicesSelected = useMemo(
    () =>
      serviceRows.some(service => service?.serviceType === ALL_SERVICES_VALUE),
    [serviceRows]
  );
  const hasMultipleServiceRows = serviceRows.length > 1;

  const buildServiceOptions = currentValue => [
    { name: 'Pasirinkti', value: '' },
    { name: 'Visos paslaugos', value: ALL_SERVICES_VALUE },
    ...serviceTypes
      .filter(
        serviceType =>
          Number(currentValue) === serviceType ||
          !selectedServiceValues.includes(serviceType)
      )
      .map(serviceType => ({
        name: mapServiceTypes(serviceType),
        value: serviceType,
      })),
  ];

  const handleServiceChange = (index, nextValue) => {
    if (nextValue === ALL_SERVICES_VALUE) {
      setServices([
        {
          serviceType: ALL_SERVICES_VALUE,
          discount: serviceRows[index]?.discount || '',
        },
      ]);
      return;
    }

    const nextRows = serviceRows.slice();
    nextRows[index] = {
      ...nextRows[index],
      serviceType: nextValue === '' ? '' : Number(nextValue),
    };
    setServices(nextRows);
  };

  const handleServiceDiscountChange = (index, nextValue) => {
    const nextRows = serviceRows.slice();
    nextRows[index] = {
      ...nextRows[index],
      discount: nextValue,
    };
    setServices(nextRows);
  };

  const handleAddServiceRow = () => {
    setServices([...serviceRows, createEmptyServiceRow()]);
  };

  const handleRemoveServiceRow = index => {
    const nextRows = serviceRows.filter((_, rowIndex) => rowIndex !== index);
    setServices(nextRows.length ? nextRows : [createEmptyServiceRow()]);
  };

  const renderField = ({
    label,
    name,
    type = 'text',
    value,
    placeholder,
    maxLength,
    min,
    max,
    step,
    disabled = isFormDisabled,
    className,
  }) => (
    <div className={className}>
      <p className={styles.fieldLabel}>{label}</p>
      <Input
        name={name}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onChange}
        maxLength={maxLength}
        min={min}
        max={max}
        step={step}
        withLabel={false}
      />
    </div>
  );

  const renderLanguageBlock = ({
    languageLabel,
    titleName,
    titleValue,
    descriptionName,
    descriptionValue,
  }) => (
    <div className={styles.languageBlock}>
      <p className={styles.fieldLabel}>{languageLabel}</p>
      <Input
        name={titleName}
        type="text"
        value={titleValue}
        placeholder="Pavadinimas"
        disabled={isFormDisabled}
        onChange={onChange}
        maxLength={LIMITS.title}
        withLabel={false}
      />
      <Input
        name={descriptionName}
        type="textarea"
        value={descriptionValue}
        placeholder="Aprašymas"
        disabled={isFormDisabled}
        onChange={onChange}
        maxLength={LIMITS.description}
        withLabel={false}
      />
    </div>
  );

  const renderSelectedCheckboxList = ({
    items,
    nameKey,
    valueKey,
    customId,
    onToggle,
    disabled = false,
  }) => (
    <div className={styles.selectedCheckboxList}>
      {items.map(item => (
        <Checkbox
          key={item[valueKey]}
          label={item[nameKey]}
          name={item[nameKey]}
          checked={true}
          value={item[valueKey]}
          onChange={() => onToggle(item[valueKey])}
          disabled={disabled}
          dense={true}
          customId={customId}
        />
      ))}
    </div>
  );

  return (
    <form onSubmit={onSubmit} method="POST">
      <fieldset className={styles.formFieldset} disabled={loading}>
        <div className={styles.formdata}>
          <div className={styles.formSection}>
            <div className={styles.checkboxRow}>
              <Checkbox
                label="Nuolaidos atvaizdavimas programėlėje"
                name="createDiscountBanner"
                checked={values.createDiscountBanner}
                value={values.createDiscountBanner}
                onChange={setCreateDiscountBanner}
                disabled={isFormDisabled}
              />
            </div>

            <div className={styles.fieldsGrid}>
              {renderField({
                label: 'Kodas',
                name: 'code',
                value: values.code,
                placeholder: 'Įvesti',
                disabled: loading || codeDisabled,
                className: styles.fullWidthField,
              })}
            </div>

            <div className={styles.fieldsGrid}>
              {renderField({
                label: 'Laikotarpis nuo',
                name: 'dateFrom',
                type: 'date',
                value: values.dateFrom,
                max: '2999-12-31',
              })}
              {renderField({
                label: 'Laikotarpis iki',
                name: 'dateTo',
                type: 'date',
                value: values.dateTo,
                max: '2999-12-31',
              })}
            </div>

            <div className={styles.fieldsGrid}>
              {renderField({
                label: 'Valandos nuo',
                name: 'timeFrom',
                type: 'time',
                value: values.timeFrom,
              })}
              {renderField({
                label: 'Valandos iki',
                name: 'timeTo',
                type: 'time',
                value: values.timeTo,
              })}
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              Detalūs kampanijų pavadinimai
            </h3>

            <div className={styles.languageFields}>
              {renderLanguageBlock({
                languageLabel: '🇱🇹 Lietuvių kalba',
                titleName: 'title_lt',
                titleValue: values.title_lt,
                descriptionName: 'description_lt',
                descriptionValue: values.description_lt,
              })}
              {renderLanguageBlock({
                languageLabel: '🇬🇧 Anglų kalba',
                titleName: 'title_en',
                titleValue: values.title_en,
                descriptionName: 'description_en',
                descriptionValue: values.description_en,
              })}
              {renderLanguageBlock({
                languageLabel: '🇷🇺 Rusų kalba',
                titleName: 'title_ru',
                titleValue: values.title_ru,
                descriptionName: 'description_ru',
                descriptionValue: values.description_ru,
              })}
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Paslaugos</h3>

            <div className={styles.serviceRows}>
              {serviceRows.map((serviceRow, index) => (
                <div
                  key={`${serviceRow?.serviceType || 'empty'}-${index}`}
                  className={styles.serviceRow}
                >
                  <div className={styles.serviceSelectField}>
                    <p className={styles.fieldLabel}>Paslauga</p>
                    <Select
                      name={`service-${index}`}
                      options={buildServiceOptions(serviceRow?.serviceType)}
                      selectedValue={serviceRow?.serviceType ?? ''}
                      onChange={event =>
                        handleServiceChange(index, event.target.value)
                      }
                      disabled={isFormDisabled}
                    />
                  </div>

                  <div className={styles.serviceDiscountField}>
                    <p className={styles.fieldLabel}>Nuolaidos dydis %</p>
                    <Input
                      name={`service-discount-${index}`}
                      type="number"
                      value={serviceRow?.discount ?? ''}
                      min="1"
                      max="100"
                      step="1"
                      disabled={isFormDisabled}
                      onChange={event =>
                        handleServiceDiscountChange(index, event.target.value)
                      }
                      withLabel={false}
                    />
                  </div>

                  <button
                    type="button"
                    className={`${styles.removeServiceButton} ${
                      hasMultipleServiceRows
                        ? styles.removeServiceButtonDanger
                        : ''
                    }`}
                    onClick={() => handleRemoveServiceRow(index)}
                    aria-label="Pašalinti paslaugą"
                    disabled={isFormDisabled}
                  >
                    <Icon
                      name="delete"
                      classes={{
                        icon: `${styles.removeServiceIcon} ${
                          hasMultipleServiceRows
                            ? styles.removeServiceIconDanger
                            : ''
                        }`,
                      }}
                    />
                  </button>
                </div>
              ))}

              <button
                type="button"
                className={styles.addServiceButton}
                onClick={handleAddServiceRow}
                disabled={
                  isFormDisabled ||
                  isAllServicesSelected ||
                  selectedServiceValues.length === serviceTypes.length
                }
              >
                Pridėti
              </button>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Kiti nustatymai</h3>

            <div className={styles.settingsGrid}>
              <div className={styles.settingsColumn}>
                <h4 className={styles.columnTitle}>Galioja</h4>
                <div className={styles.selectionList}>
                  <MultiselectAll
                    array={availableWeekdays}
                    type="object"
                    valueType="number"
                    allText="Visomis dienomis"
                    state={weekdays}
                    setState={setWeekdays}
                  />
                </div>
              </div>

              <div className={styles.settingsColumn}>
                <h4 className={styles.columnTitle}>Klientų grupė</h4>
                <WithSpinner loading={clientGroupsLoading}>
                  <div className={styles.selectorWithSelections}>
                    <MultiSelectDropdown
                      array={clientGroupOptions}
                      name="name"
                      value="id"
                      valueType="number"
                      allText="Visos klientų grupės"
                      state={groups}
                      setState={setGroups}
                      placeholder="Pasirinkti"
                      disabled={isFormDisabled}
                    />

                    {renderSelectedCheckboxList({
                      items: selectedClientGroupOptions,
                      nameKey: 'name',
                      valueKey: 'id',
                      customId: 'discount-client-group',
                      disabled: isFormDisabled,
                      onToggle: selectedId =>
                        setGroups(
                          groups.filter(groupId => groupId !== selectedId)
                        ),
                    })}
                  </div>
                </WithSpinner>
              </div>

              <div className={styles.settingsColumn}>
                <h4 className={styles.columnTitle}>Lokacijos</h4>
                <WithSpinner loading={locationsLoading}>
                  <div className={styles.selectorWithSelections}>
                    <MultiSelectDropdown
                      array={availableLocations}
                      name="full_address"
                      value="id"
                      valueType="number"
                      allText="Visos lokacijos"
                      state={locations}
                      setState={setLocations}
                      placeholder="Pasirinkti"
                      disabled={isFormDisabled}
                    />

                    {renderSelectedCheckboxList({
                      items: selectedLocationOptions,
                      nameKey: 'full_address',
                      valueKey: 'id',
                      customId: 'discount-location',
                      disabled: isFormDisabled,
                      onToggle: selectedId =>
                        setLocations(
                          locations.filter(
                            locationId => locationId !== selectedId
                          )
                        ),
                    })}
                  </div>
                </WithSpinner>
              </div>
            </div>

            <div className={styles.usageRow}>
              <div className={styles.usageTypeField}>
                <p className={styles.fieldLabel}>Tipas</p>
                <Select
                  name="type"
                  options={discountUsageTypes}
                  selectedValue={Number(values.type)}
                  onChange={event =>
                    onChange({
                      target: {
                        name: 'type',
                        value: Number(event.target.value),
                      },
                    })
                  }
                  disabled={isFormDisabled}
                />
              </div>

              {isMultiUseDiscount && (
                <div className={styles.usageCountField}>
                  <p className={styles.fieldLabel}>Kiek kartų?</p>
                  <Input
                    name="usage_limit"
                    type="number"
                    value={values.usage_limit}
                    min="1"
                    step="1"
                    disabled={isFormDisabled}
                    onChange={onChange}
                    withLabel={false}
                  />
                </div>
              )}
            </div>
          </div>

          {values.createDiscountBanner && (
            <div className={styles.formSection}>
              <Divider className={styles.sectionDivider} />
              <h3 className={styles.sectionTitle}>Banerių nustatymai</h3>

              <div className={styles.bannerGrid}>
                <div className={styles.bannerColumn}>
                  <h4 className={styles.columnTitle}>Kur bus rodoma?</h4>
                  <div className={styles.selectionList}>
                    <MultiselectAll
                      array={bannerPlacementOptions}
                      type="object"
                      name="name"
                      value="value"
                      valueType="string"
                      showAll={false}
                      state={values.bannerPlacements}
                      setState={nextPlacements =>
                        onChange({
                          target: {
                            name: 'bannerPlacements',
                            value: nextPlacements,
                          },
                        })
                      }
                    />
                  </div>
                </div>

                <div className={styles.bannerColumn}>
                  <h4 className={styles.columnTitle}>
                    Įkelti banerio nuotrauką
                  </h4>
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/png, image/jpeg"
                    className={styles.hiddenInput}
                    onChange={handleBannerFileChange}
                  />
                  <button
                    type="button"
                    className={styles.bannerUpload}
                    onClick={openBannerInput}
                    disabled={isFormDisabled || bannerLoading}
                  >
                    <Icon
                      name="add"
                      classes={{ icon: styles.bannerUploadIcon }}
                    />
                    <span>Įkelti banerio nuotrauką</span>
                  </button>

                  {bannerFileName && (
                    <div className={styles.bannerMeta}>
                      {bannerFileUrl ? (
                        <a
                          href={bannerFileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.bannerLink}
                        >
                          {bannerFileName}
                        </a>
                      ) : (
                        <span>{bannerFileName}</span>
                      )}
                      <button
                        type="button"
                        className={styles.bannerRemove}
                        onClick={handleBannerRemove}
                      >
                        Pašalinti
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </fieldset>

      <SubmitActions
        back={config.APP_PATHS.discounts}
        loading={loading || bannerLoading}
        cancel="Atgal"
      />
    </form>
  );
};

const mapStateToProps = state => ({
  availableLocations: state.locations.locations,
  locationsLoading: state.locations.loading,
  clientGroups: state.clientGroups.groups,
  clientGroupsLoading: state.clientGroups.loading,
  discountBanner: state.discounts.discountBanner,
  bannerLoading: state.discounts.bannerUploading,
});

Form.propTypes = {
  dispatch: PropTypes.func.isRequired,
  values: PropTypes.shape({
    code: PropTypes.string.isRequired,
    title_lt: PropTypes.string.isRequired,
    title_en: PropTypes.string.isRequired,
    title_ru: PropTypes.string.isRequired,
    description_lt: PropTypes.string.isRequired,
    description_en: PropTypes.string.isRequired,
    description_ru: PropTypes.string.isRequired,
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired,
    timeFrom: PropTypes.string.isRequired,
    timeTo: PropTypes.string.isRequired,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    usage_limit: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    createDiscountBanner: PropTypes.bool.isRequired,
    bannerPlacements: PropTypes.arrayOf(PropTypes.string).isRequired,
    announcement: PropTypes.shape({
      banner: PropTypes.object,
      banner_id: PropTypes.number,
    }).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBannerChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  codeDisabled: PropTypes.bool,
  availableLocations: PropTypes.arrayOf(PropTypes.object).isRequired,
  locationsLoading: PropTypes.bool.isRequired,
  clientGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  clientGroupsLoading: PropTypes.bool.isRequired,
  weekdays: PropTypes.arrayOf(PropTypes.number).isRequired,
  setWeekdays: PropTypes.func.isRequired,
  services: PropTypes.arrayOf(
    PropTypes.shape({
      serviceType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  setServices: PropTypes.func.isRequired,
  locations: PropTypes.arrayOf(PropTypes.number).isRequired,
  setLocations: PropTypes.func.isRequired,
  groups: PropTypes.arrayOf(PropTypes.number).isRequired,
  setGroups: PropTypes.func.isRequired,
  setCreateDiscountBanner: PropTypes.func.isRequired,
  discountBanner: PropTypes.object.isRequired,
  bannerLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(Form);
